import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
  HostListener,
} from '@angular/core';
import { IconsModule } from '../../../icons/icons.module';
import {
  NgbDropdownModule,
  NgbModal,
  NgbModalModule,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import IZikataClientProfile, {
  UserInfoConfigService,
  UserInfoConfig,
  SenderId,
  Organization,
  Member,
} from '../../../services/user-info-config/user-info-config.service';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { SenderStateService } from '../../../services/sender-state/sender-state.service';
import { OrgStateService } from '../../../services/organization-state/org-state.service';

interface ViewModel {
  config: UserInfoConfig;
  orgs: Organization[];
  selectedOrg: Organization | null;
  isMobile: boolean;
}


@Component({
  selector: 'zikatagov-user-info',
  template: `
    <div
      class="d-flex align-items-center text-light"
      *ngIf="viewModel$ | async as vm"
    >
      <!-- Mobile view toggle button -->
      <button
        *ngIf="vm.isMobile"
        class="btn btn-sm btn-link d-md-none ms-auto mobile-menu-toggle"
        (click)="toggleMobileMenu()"
      >
        <ng-icon name="saxMenu1Outline" size="20" class="text-white"></ng-icon>
      </button>

      <!-- Desktop view or expanded mobile menu -->
      <div
        [class.d-none]="vm.isMobile && !showMobileMenu"
        [class.mobile-menu]="vm.isMobile && showMobileMenu"
        class="d-flex align-items-end"
        [class.flex-column]="vm.isMobile"
        [class.justify-content-end]="!vm.isMobile"
        [class.bg-plum]="vm.isMobile"
        [class.w-100]="vm.isMobile"
      >
        <!-- Notification icon -->
        <div [class.mb-3]="vm.isMobile" class="d-flex align-items-center">
          <ng-icon
            name="saxNotificationOutline"
            class="text-white me-3"
            size="24"
          ></ng-icon>

          <div
            class="vr bg-white border-white me-3 mt-2"
            style="height: 35px; width: 2px;"
            [class.d-none]="vm.isMobile"
          ></div>
        </div>

        <!-- ORG dropdown -->
        <div
          *ngIf="vm.config.showSenderIds"
          ngbDropdown
          [class.mb-3]="vm.isMobile"
          class="sender d-inline-flex align-items-center mr-2"
        >
          <button
            class="btn btn-sm btn-outline-light"
            id="dropdownBasic1"
            ngbDropdownToggle
          >
            {{ vm.selectedOrg?.name || 'Select a Sender ID' }}
          </button>
          <div ngbDropdownMenu aria-labelledby="dropdownBasic1">
            <button
              *ngFor="let option of vm.orgs"
              (click)="orgSelected(option)"
              ngbDropdownItem
            >
              {{ option.name }}
            </button>
          </div>
        </div>

        <!-- Sender ID dropdown -->
        <div
          *ngIf="vm.config.showSenderIds"
          ngbDropdown
          [class.mb-3]="vm.isMobile"
          class="sender d-inline-flex align-items-center"
        >
          <button
            class="btn btn-sm btn-outline-light"
            id="dropdownBasic1"
            ngbDropdownToggle
          >
            {{ currentSender?.senderName || 'Select a Sender ID' }}
          </button>
          <div ngbDropdownMenu aria-labelledby="dropdownBasic1">
            <button
              *ngFor="let option of vm.selectedOrg?.senderIds ?? []"
              (click)="senderIdSelected(option)"
              ngbDropdownItem
            >
              {{ option.senderName }}
            </button>
          </div>
        </div>

        <!-- Balance section -->
        <div
          *ngIf="vm.config.showBalance"
          [class.mb-3]="vm.isMobile"
          class="d-inline-flex align-items-center mx-3"
        >
          <div class="d-flex flex-column">
            <small class="text-white"> Balance </small>
            <span class="text-white fw-bold"
              >Units: {{ currentSenderIdBalance }}</span
            >
          </div>
          <i
            (click)="loadBalance()"
            class="fa fa-refresh ms-2"
            aria-hidden="true"
          ></i>
        </div>

        <!-- Top up button -->
        <div
          *ngIf="vm.config.showTopUpButton"
          [class.mb-3]="vm.isMobile"
          class="d-inline-flex align-items-center"
        >
          <button
            (click)="openTopUpModal(currentSender)"
            type="button"
            class="btn btn-sm btn-outline-light rounded"
          >
            Top Up
          </button>
        </div>

        <!-- Divider before user info -->

        <!-- User info section -->
        <div class="d-inline-flex align-items-center">
          <div
            class="vr bg-white ms-3 me-3 mt-3"
            style="height: 35px; width: 2px;"
            [class.d-none]="vm.isMobile"
          ></div>

          <div class="d-flex flex-column align-items-end text-end me-3">
            <span class="font-weight-normal">{{ userProfile?.firstName??member?.firstName }}</span>
            <small class="font-weight-light">{{ member?.role?.toLowerCase() }}</small>
          </div>

          <div
            class="rounded-circle overflow-hidden bg-white"
            [ngStyle]="{ width: avatarSize + 'px', height: avatarSize + 'px' }"
          >
            <img
              *ngIf="avatarSrc"
              [src]="avatarSrc"
              [alt]="userProfile?.firstName"
              class="img-fluid"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Modal template -->
    <ng-template #content let-modal>
      <div class="border-0 bg-secondary d-flex justify-content-end">
        <button
          type="button"
          class="btn-sm btn-close me-2 mt-1"
          aria-label="Close"
          (click)="modal.dismiss('Cross click')"
        ></button>
      </div>
      <div class="modal-body px-4 py-0 bg-secondary">
        <!-- Top heading part -->
        <h2 class="mb-0 fw-bold text-center text-plum-dark">M-Pesa Paybill</h2>
        <!-- Smaller font for the second part -->
        <h6 class="text-center text-plum-dark fs-6">
          Payment Instructions for {{ currentSender?.senderName }} Top Up
        </h6>
        <div class="row justify-content-center">
          <div class="col-12 col-md-8 col-lg-6">
            <ol class="list-unstyled">
              <li class="text-plum-dark">
                <small>1. Go to Safaricom Menu</small>
              </li>
              <li class="text-plum-dark"><small>2. Select M-Pesa</small></li>
              <li class="text-plum-dark">
                <small>3. Choose Lipa na M-Pesa</small>
              </li>
              <li class="text-plum-dark"><small>4. Select Paybill</small></li>
              <li class="text-plum-dark">
                <small
                  >5. Enter Paybill Number:
                  <span class="fw-bold">4118635</span></small
                >
              </li>
              <li class="text-plum-dark">
                <small
                  >6. Enter Account Number:
                  <span class="fw-bold">{{
                    currentSender?.senderName
                  }}</span></small
                >
              </li>
              <li class="text-plum-dark"><small>7. Enter Amount</small></li>
              <li class="text-plum-dark"><small>8. Enter PIN</small></li>
              <li class="text-plum-dark">
                <small>9. Complete Transaction</small>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </ng-template>
  `,
  standalone: true,
  imports: [CommonModule, IconsModule, NgbDropdownModule, NgbModalModule],
  styles: [
    `
      :host {
        display: inline-block;
        width: 100%;
      }
      .fa-refresh {
        cursor: pointer;
      }
      .mobile-menu {
        position: absolute;
        top: 100%;
        right: 0;
        padding: 1rem;
        border-radius: 0 0 0.25rem 0.25rem;
        z-index: 1000;
        width: 100%;
        max-width: 300px;
        box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
      }
      .mobile-menu-toggle {
        padding: 0.25rem 0.5rem;
      }
    `,
  ],
})
export class UserInfoComponent implements OnInit {
  @Input() userProfile: IZikataClientProfile | null | undefined = null;
  @Input() userRole = '';
  @Input() avatarSrc = 'assets/images/Ellipse 1.svg';
  @Input() avatarSize = 32;

  @ViewChild('content') private modalContent!: TemplateRef<any>;

  private selectedSenderSubject = new BehaviorSubject<SenderId | null>(null);
  private selectedOrgSubject = new BehaviorSubject<Organization | null>(null);

  selectedOrg$ = this.selectedOrgSubject.asObservable();

  private isMobileSubject = new BehaviorSubject<boolean>(false);
  isMobile$ = this.isMobileSubject.asObservable();

  showMobileMenu = false;
  viewModel$: Observable<ViewModel>;
  currentSender: SenderId | null = null;
  currentSenderIdBalance: number | null = null;
  member: Member | null | undefined = null;

  constructor(
    private modalService: NgbModal,
    private userInfoConfigService: UserInfoConfigService,
    private senderStateService: SenderStateService,
    private orgStateService: OrgStateService
  ) {
    this.checkScreenSize();
    this.viewModel$ = this.initViewModel();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize();
  }

  checkScreenSize() {
    this.isMobileSubject.next(window.innerWidth < 768);
    if (window.innerWidth >= 768) {
      this.showMobileMenu = false;
    }
  }

  toggleMobileMenu() {
    this.showMobileMenu = !this.showMobileMenu;
  }

  ngOnInit() {
    this.currentSenderIdBalance = null;
    const currentOrg = this.orgStateService.getCurrentOrg();

    if (currentOrg) {
      this.loadMember(this.userProfile, currentOrg.id);
      this.selectedOrgSubject.next(currentOrg);
    } else {
      this.initializeOrg();
    }
  }

  private initViewModel(): Observable<ViewModel> {
    return combineLatest({
      config: this.userInfoConfigService.getConfig(),
      orgs: this.userInfoConfigService.getOrgs(),
      selectedOrg: this.selectedOrg$,
      isMobile: this.isMobile$,
    }).pipe(
      map(({ config, orgs, selectedOrg, isMobile }) => ({
        config,
        orgs,
        selectedOrg,
        isMobile,
      }))
    );
  }

  private initializeFirstSender() {
    this.userInfoConfigService
      .getSenderIds()
      .pipe(take(1))
      .subscribe((senderIds) => {
        if (senderIds.length > 0) {
          this.senderIdSelected(senderIds[0]);
        }
      });
  }

  private initializeOrg() {
    this.userInfoConfigService
      .getOrgs()
      .pipe(take(1))
      .subscribe((orgs) => {
        if (orgs.length > 0) {
          this.orgSelected(orgs[0]);
          this.loadMember(this.userProfile, orgs[0].id);
        }
      });
  }

  senderIdSelected(sender: SenderId) {
    console.log('Selected sender:', sender);
    this.selectedSenderSubject.next(sender);
    this.senderStateService.setCurrentSender(sender);
    if (this.isMobileSubject.getValue()) {
      this.showMobileMenu = false;
    }
    this.currentSender = sender;
    this.userInfoConfigService.getBalance(sender.id).subscribe({
      next: (balance) => {
        this.currentSenderIdBalance = balance;
      },
      error: (error) => {
        console.error('Error loading user account:', error);
      },
    });
  }

  loadBalance() {
    const currentSender = this.selectedSenderSubject.getValue();
    if (currentSender) {
      this.userInfoConfigService.getBalance(currentSender.id).subscribe(
        (balance) => {
          this.selectedSenderSubject.next({ ...currentSender });
        },
        (error) => console.error('Error loading balance:', error)
      );
    }
  }

  openTopUpModal(selectedSender: SenderId | null) {
    if (selectedSender) {
      if (this.isMobileSubject.getValue()) {
        this.showMobileMenu = false;
      }
      const modalRef: NgbModalRef = this.modalService.open(this.modalContent, {
        centered: true,
        size: 'lg',
      });

      modalRef.result.then(
        (result: any) => {
          console.log('Modal closed with result:', result);
          // Handle any post-close logic here
        },
        (reason: any) => {
          console.log('Modal dismissed with reason:', reason);
          // Handle any dismiss logic here
        }
      );
    } else {
      console.error('No sender selected');
    }
  }

  protected orgSelected(organization: Organization) {
    console.log('Selected organization:', organization);
    this.selectedOrgSubject.next(organization);
    this.orgStateService.setCurrentOrg(organization);
    if (this.isMobileSubject.getValue()) {
      this.showMobileMenu = false;
    }
    if (organization.senderIds.length > 0) {
      this.senderIdSelected(organization.senderIds[0]);
    } else {
      this.selectedSenderSubject.next(null);
      this.currentSender = null;
    }

    organization.members.forEach((member) => {
      if (member.userId === this.userProfile?.id) {
        this.userRole = member.role;
      }
    });
  }

  private loadMember(userProfile: IZikataClientProfile | null | undefined, organizationId: string) {
    if (userProfile?.id) {
      this.userInfoConfigService
        .getMemberRole(organizationId, userProfile?.id)
        .subscribe({
          next: (res) => {
            this.member = res;
          }
        })
    }
  }
}
